import {
  ArrowRight,
  Cloud,
  Gamepad2,
  LockKeyhole,
  Sparkles,
  Star,
  Target,
  Trophy,
  Zap,
} from "lucide-react";
import styles from "./ActivityView.module.css";

interface ActivityViewProps {
  onShowFriends: () => void;
  onStartPlaying: () => void;
}

const quests = [
  {
    icon: Gamepad2,
    label: "Spela ett spel",
    reward: "+40 XP",
    tone: "coral",
  },
  {
    icon: Target,
    label: "Samla 4 spelare",
    reward: "+25 XP",
    tone: "mint",
  },
  {
    icon: Trophy,
    label: "Vinn en runda",
    reward: "+35 XP",
    tone: "amber",
  },
] as const;

export function ActivityView({
  onShowFriends,
  onStartPlaying,
}: ActivityViewProps) {
  return (
    <main className={styles.view}>
      <header className={styles.header}>
        <div>
          <span className={styles.eyebrow}>Din spelresa</span>
          <h1>Aktivitet</h1>
        </div>
        <div className={styles.guestBadge} aria-label="Du spelar som gäst">
          <span className={styles.guestDot} aria-hidden="true" />
          Gäst
        </div>
      </header>

      <section className={styles.levelCard} aria-labelledby="level-heading">
        <div className={styles.levelGlow} aria-hidden="true" />
        <div className={styles.levelTop}>
          <div className={styles.levelMedallion}>
            <Star aria-hidden="true" />
            <span>1</span>
          </div>
          <div className={styles.levelCopy}>
            <span>Nuvarande nivå</span>
            <h2 id="level-heading">Feststartare</h2>
          </div>
          <span className={styles.xpPill}>
            <Zap aria-hidden="true" />
            0 XP
          </span>
        </div>

        <div className={styles.progressMeta}>
          <span>Nästa: Isbrytare</span>
          <span>0 / 100 XP</span>
        </div>
        <div
          className={styles.progressTrack}
          role="progressbar"
          aria-label="Progress mot nivå 2"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={0}
        >
          <span />
        </div>

        <button className={styles.syncHint} type="button" onClick={onShowFriends}>
          <Cloud aria-hidden="true" />
          <span>
            <strong>Spara din spelresa</strong>
            Logga in så synkas XP och badges
          </span>
          <ArrowRight aria-hidden="true" />
        </button>
      </section>

      <section className={styles.questSection} aria-labelledby="quests-heading">
        <div className={styles.sectionHeading}>
          <div>
            <span className={styles.eyebrow}>3 steg · 100 XP</span>
            <h2 id="quests-heading">Kvällens första quest</h2>
          </div>
          <Sparkles aria-hidden="true" />
        </div>

        <ol className={styles.questList}>
          {quests.map((quest, index) => {
            const Icon = quest.icon;

            return (
              <li
                className={styles.quest}
                data-tone={quest.tone}
                key={quest.label}
              >
                <span className={styles.questNumber}>{index + 1}</span>
                <span className={styles.questIcon}>
                  <Icon aria-hidden="true" />
                </span>
                <span className={styles.questText}>
                  <strong>{quest.label}</strong>
                  <span>{quest.reward}</span>
                </span>
                <LockKeyhole aria-label="Låst tills uppgiften är klar" />
              </li>
            );
          })}
        </ol>

        <button
          className={styles.primaryButton}
          type="button"
          onClick={onStartPlaying}
        >
          Starta ett spel
          <ArrowRight aria-hidden="true" />
        </button>
      </section>

      <section className={styles.emptyActivity} aria-labelledby="recent-heading">
        <span className={styles.emptyIcon}>
          <Zap aria-hidden="true" />
        </span>
        <div>
          <h2 id="recent-heading">Här blir det liv</h2>
          <p>
            Dina spel, vinster och nya rekord samlas här efter första rundan.
          </p>
        </div>
      </section>
    </main>
  );
}
